import type { IndustryTaxonomyFilterDto } from "app/pages/sales-intelligence/domain/industry/types/filter-values";
import type {
    IndustryNodeId,
    IndustryTaxonomyIndex,
} from "app/pages/sales-intelligence/domain/industry/types/taxonomy";
import { canonicaliseValues, impliedLeafIds } from "app/pages/sales-intelligence/domain/industry/utils/taxonomy";
import { FilterPanelBaseFilter } from "app/pages/sales-intelligence/modules/advanced-search-filtering/models/BaseFilter";
import type { IFilterPanelBaseFilterDeps } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filters";
import type { FilterValueDelta } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/misc";
import { createIndustrySearchIndex } from "app/pages/sales-intelligence/modules/industry/services/search/createIndustrySearchIndex";
import type { IIndustrySearchIndex } from "app/pages/sales-intelligence/modules/industry/services/search/types";
import { type InclusionOption, inclusionOptionMap } from "app/pages/sales-intelligence/types/commonMaps";

const difference = (a: ReadonlySet<IndustryNodeId>, b: ReadonlySet<IndustryNodeId>): IndustryNodeId[] =>
    [...a].filter((id) => !b.has(id));

class IndustryTaxonomyFilterModel extends FilterPanelBaseFilter<
    IndustryTaxonomyFilterDto,
    IndustryTaxonomyIndex,
    IndustryTaxonomyFilterDto
> {
    /** Stable for the model's lifetime, so the fuzzy index normalises the taxonomy at most once. */
    readonly searchIndex: IIndustrySearchIndex;

    private readonly defaultInclusion: InclusionOption = inclusionOptionMap.includeOnly;

    constructor(deps: IFilterPanelBaseFilterDeps<IndustryTaxonomyFilterDto, IndustryTaxonomyIndex>) {
        super(deps);

        this.searchIndex = createIndustrySearchIndex();
    }

    fromApiValue(value?: IndustryTaxonomyFilterDto): IndustryTaxonomyFilterDto {
        return {
            inclusion: value?.inclusion ?? this.defaultInclusion,
            values: canonicaliseValues(value?.values ?? [], this.options),
        };
    }

    toApiValue(value?: IndustryTaxonomyFilterDto): IndustryTaxonomyFilterDto | undefined {
        return value?.values.length ? value : undefined;
    }

    getEmptyValue(): IndustryTaxonomyFilterDto | undefined {
        return undefined;
    }

    isValueEmpty(value?: IndustryTaxonomyFilterDto): boolean {
        return !value || value.values.length === 0;
    }

    /**
     * Short-circuit on DTO-reference identity *before* `fromApiValue`, so an unrelated filter's toggle
     * (which preserves this key's reference) skips the whole taxonomy walk. The base runs `fromApiValue`
     * on both sides first, which would defeat a local-value comparison.
     */
    calculateValueDeltaFromDto(
        previousApiValue?: IndustryTaxonomyFilterDto,
        newApiValue?: IndustryTaxonomyFilterDto,
    ): FilterValueDelta {
        if (previousApiValue === newApiValue) {
            return { deleted: [] };
        }

        return this.calculateValueDelta(this.fromApiValue(previousApiValue), this.fromApiValue(newApiValue));
    }

    /**
     * Diffs the *implied leaves*, not the raw ids: checking a parent collapses its children away, so a
     * raw-id diff would report a widening selection as deletions. An inclusion flip inverts the whole
     * selection, so the previous leaves all count as removed.
     */
    calculateValueDelta = (
        previousValue?: IndustryTaxonomyFilterDto,
        newValue?: IndustryTaxonomyFilterDto,
    ): FilterValueDelta => {
        const previousLeaves = impliedLeafIds(previousValue?.values ?? [], this.options);

        if (previousValue && newValue && previousValue.inclusion !== newValue.inclusion) {
            return { deleted: [...previousLeaves] };
        }

        const newLeaves = impliedLeafIds(newValue?.values ?? [], this.options);

        return { deleted: difference(previousLeaves, newLeaves) };
    };
}

export default IndustryTaxonomyFilterModel;
